import axios from "axios";
import { UserDataType } from "..";
import { GetAccessToken } from "../api/auth/Authentication";

const authHeader = async (): Promise<{ Authorization: string }> => {
  const accessToken = localStorage.getItem("access_token");
  try {
    if (accessToken) {
      const tokenType = localStorage.getItem("token_type");
      const expiredAt = new Date(
        Date.parse(localStorage.getItem("expired_at") ?? "0")
      );
      const currentTime = new Date();
      if (currentTime.getTime() >= expiredAt.getTime()) {
        const refreshToken = localStorage.getItem("refresh_token");
        let payload: any = {
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        };
        const data = (await GetAccessToken(payload)) as UserDataType;
        localStorage.setItem("token_type", data.tokenType);
        localStorage.setItem("access_token", data.accessToken);
        localStorage.setItem("refresh_token", data.refreshToken);
        localStorage.setItem("expired_at", data.expiredAt);
        return authHeader();
      }
      return { Authorization: `${tokenType} ${accessToken}` };
    } else {
      throw new Error("Unauthorized");
    }
  } catch (e) {
    throw handleError(e);
  }
};

const handleError = (err: any) => {
  localStorage.removeItem("token_type");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("extension");
  localStorage.removeItem("user");
  window.location.reload();
  throw err;
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_CLAY_STONE,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

const get = async <T,>(
  url: string,
  params: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const headers: any = {
    "Content-Type": "application/json",
  };
  if (withAuth) {
    try {
      const auth = await authHeader();
      Object.assign(headers, auth);
    } catch (e) {
      return e as T;
    }
  }

  return instance
    .get(url, {
      params,
      headers,
    })
    .then((response) => response.data);
};

const post = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true,
  withUrlendcode: boolean = false,
  withFromData: boolean = false
): Promise<T> => {
  const headers: any = {
    "Content-Type": withFromData
      ? "multipart/form-data"
      : withUrlendcode
      ? "application/x-www-form-urlencoded"
      : "application/json",
  };

  const config = {
    headers,
  };

  if (withAuth) {
    try {
      const auth = await authHeader();
      Object.assign(config.headers, auth);
    } catch (e) {
      return e as T;
    }
  }

  return instance.post(url, data, config).then((response) => response.data);
};

const put = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const headers: any = {
    "Content-Type": "application/json",
  };
  const config = {
    headers,
  };

  if (withAuth) {
    try {
      const auth = await authHeader();
      Object.assign(config.headers, auth);
    } catch (e) {
      return e as T;
    }
  }

  return instance.put(url, data, config).then((response) => response.data);
};

const patch = async <T,>(
  url: string,
  data: any = {},
  withAuth: boolean = true
): Promise<T> => {
  const headers: any = {
    "Content-Type": "application/json",
  };
  const config = {
    headers,
  };

  if (withAuth) {
    try {
      const auth = await authHeader();
      Object.assign(config.headers, auth);
    } catch (e) {
      return e as T;
    }
  }

  return instance.patch(url, data, config).then((response) => response.data);
};

const del = async <T,>(url: string, withAuth: boolean = true): Promise<T> => {
  const headers: any = {
    "Content-Type": "application/json",
  };
  const config = {
    headers,
  };

  if (withAuth) {
    try {
      const auth = await authHeader();
      Object.assign(config.headers, auth);
    } catch (e) {
      return e as T;
    }
  }

  return instance.delete(url, config).then((response) => response.data);
};

export const httpClayStone = {
  get,
  post,
  put,
  patch,
  del,
};
